import {
  Box,
  Button,
  Divider,
  FlexGrid,
  FlexGridColumn,
  Icon,
  Stack,
} from '@hs-baumappe/legacy-ui';
import { Form, FormikHelpers, FormikProvider, useFormik } from 'formik';
import { ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useAssistantDocument } from '@hs-baumappe/llm-ui';
import { Employee } from '../../hooks/useProjectEmployees/graphql/__generated__/Employee.fragment';
import EmployeesSection from './components/EmployeesSection';
import LegacyEmployeesSection from './components/LegacyEmployeesSection';
import OvertimeUploadImageWidget from './components/OvertimeUploadImageWidget';
import ProjectInformationSection from './components/ProjectInformationSection';
import WorkInformationSection from './components/WorkInformationSection';
import {
  castOvertimeFormValues,
  createLegacyOvertimeValidationSchema,
  createOvertimeValidationSchema,
  getOvertimeFormInitialValues,
  OvertimeFormValues,
} from './form';

type OvertimeFormMode = 'create' | 'edit';

interface OvertimeFormProps {
  mode: OvertimeFormMode;
  projectId: string;
  employees: Employee[];
  professions: string[];
  initialValues?: Partial<OvertimeFormValues>;
  nextSuggestedNumber?: number;
  disableProjectNumberField?: boolean;
  submitButtonText?: ReactNode;
  loading?: boolean;
  onSubmit: (values: OvertimeFormValues, formikHelpers: FormikHelpers<OvertimeFormValues>) => void;
  onDirtyStateChange?: (dirty: boolean) => void;
  pdfRedesignEnabled: boolean;
  employeeAutomaticRoleSelection: boolean;
}

const contextId = 'OVERTIME';

export default function OvertimeForm({
  mode,
  projectId,
  employees,
  professions,
  initialValues,
  nextSuggestedNumber,
  disableProjectNumberField,
  submitButtonText,
  loading,
  onSubmit,
  onDirtyStateChange,
  pdfRedesignEnabled,
  employeeAutomaticRoleSelection,
}: OvertimeFormProps): JSX.Element {
  const { t } = useTranslation();

  const validationSchema = pdfRedesignEnabled
    ? createOvertimeValidationSchema()
    : createLegacyOvertimeValidationSchema();

  const formik = useFormik({
    validationSchema,
    initialValues: getOvertimeFormInitialValues({
      initialValues,
      pdfRedesignEnabled,
      employeeAutomaticRoleSelection,
    }),
    validateOnChange: false,
    validateOnBlur: false,
    validateOnMount: mode === 'edit',
    onSubmit: (formValues, formikHelpers) =>
      onSubmit(castOvertimeFormValues(formValues), formikHelpers),
  });

  useAssistantDocument({
    contextId,
    projectId,
    // @ts-expect-error wrong types
    documentData: formik.values,
    onDocumentData: ({ data: { document } }) => {
      // @ts-expect-error wrong types here
      formik.setValues(document);
    },
  });

  const employeeNames = employees.map((employee) => employee.name);

  useEffect(() => onDirtyStateChange?.(formik.dirty), [formik.dirty, onDirtyStateChange]);

  return (
    <FormikProvider value={formik}>
      <Form>
        <FlexGrid>
          <FlexGridColumn sm={8}>
            <Stack gap="medium">
              <ProjectInformationSection
                nextSuggestedNumber={nextSuggestedNumber}
                professions={professions}
                disableProjectNumberField={disableProjectNumberField}
                pdfRedesignEnabled={pdfRedesignEnabled}
              />

              <Divider />

              <WorkInformationSection pdfRedesignEnabled={pdfRedesignEnabled} />

              <Divider />

              <OvertimeUploadImageWidget projectId={projectId} />

              <Divider />

              {pdfRedesignEnabled ? (
                <EmployeesSection
                  employees={employees}
                  employeeAutomaticRoleSelection={employeeAutomaticRoleSelection}
                />
              ) : (
                <LegacyEmployeesSection employeeNames={employeeNames} />
              )}
            </Stack>
          </FlexGridColumn>
          <FlexGridColumn sm={4}>
            <Box stylex={{ position: 'sticky', top: '0', paddingEnds: 'medium' }}>
              <Button
                type="submit"
                color="primary"
                className="qa-overtime-form-submit-button"
                fullWidth
                endIcon={<Icon name="add" />}
                loading={loading}
              >
                {submitButtonText || t('overtimeForm.submitButton')}
              </Button>
            </Box>
          </FlexGridColumn>
        </FlexGrid>
      </Form>
    </FormikProvider>
  );
}
